import { getSourceBinaryId } from '@ctw/shared/api/fhir/binaries';
import {
  codeableConceptLabel,
  codeableConceptTextOrDisplay,
} from '@ctw/shared/api/fhir/codeable-concept';
import { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { getBinaryIDFromProvenance } from '@ctw/shared/api/fhir/provenance';
import type { ResourceMap } from '@ctw/shared/api/fhir/types';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { Basic, Procedure, Provenance } from 'fhir/r4';
import { compact, startCase } from 'lodash-es';

export class ProcedureModel extends FHIRModel<Procedure> {
  public kind = 'Procedure' as const;

  public binaryId?: string;
  public provenance?: Array<Provenance>;

  public constructor(
    resource: Procedure,
    provenance?: Array<Provenance>,
    includedResources?: ResourceMap,
    basics?: Array<Basic>,
  ) {
    super(resource, includedResources, basics);
    this.provenance = provenance;
    this.binaryId = getBinaryIDFromProvenance(this.provenance);
  }

  public get title() {
    return codeableConceptTextOrDisplay(this.resource.code) || 'Unknown Procedure';
  }

  public get date() {
    return this.resource.performedDateTime ?? this.resource.performedPeriod?.start;
  }

  public get binary() {
    return this.binaryId;
  }

  // The source document's Binary id, wrapped in an array to feed RelatedDocumentsList;
  // empty when this procedure has no resolvable source Binary.
  public get relatedDocumentBinaryIds(): Array<string> {
    return compact([getSourceBinaryId(this.provenance ?? [], this)]);
  }

  public get statusDisplay(): string {
    return startCase(this.resource.status);
  }

  // `performed[x]` is polymorphic (dateTime | Period | Age | Range | string). Checks each
  // variant in turn, most-specific first, and formats it for display.
  public get performedDisplay(): string | undefined {
    if (this.resource.performedDateTime) {
      return formatFHIRDate(this.resource.performedDateTime);
    }

    if (this.resource.performedPeriod) {
      const performedPeriodDate =
        this.resource.performedPeriod.start ?? this.resource.performedPeriod.end;

      return performedPeriodDate ? formatFHIRDate(performedPeriodDate) : undefined;
    }

    if (this.resource.performedAge) {
      return this.resource.performedAge.value?.toString();
    }

    if (this.resource.performedRange) {
      return this.resource.performedRange.low?.value?.toString();
    }

    return this.resource.performedString;
  }

  public get performers(): Array<string> {
    return compact(this.resource.performer?.map((performer) => performer.actor?.display));
  }

  public get category(): string {
    return codeableConceptTextOrDisplay(this.resource.category);
  }

  public get bodySites(): Array<string> {
    return this.resource.bodySite?.map((bodySite) => codeableConceptLabel(bodySite)) ?? [];
  }

  public get reasons(): Array<string> {
    return compact(this.resource.reasonCode?.map((reason) => codeableConceptTextOrDisplay(reason)));
  }

  public get outcome(): string {
    return codeableConceptTextOrDisplay(this.resource.outcome);
  }
}
